import {
  AdminHeader,
  AdminSpinner,
  ListSpinner,
  SimpleLocationEntity,
  Tab,
  TabsWrapper,
  useAuthenticatedAuthUser,
  useSupportTicketOpenCount,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  ALL_LOCATIONS,
  SupportTicketFilters,
} from './components/SupportTicketFilters';
import { SupportTicketModal } from './components/SupportTicketModal';
import { SupportTicketRow } from './components/SupportTicketRow';
import { SUPPORT_TICKET_TABS } from './helpers/supportTicketLabels';
import { useSupportTicket } from './hooks/useSupportTicket';
import { useSupportTicketFilters } from './hooks/useSupportTicketFilters';
import { useClaimTicket } from './hooks/useSupportTicketActions';
import { useSupportTicketsList } from './hooks/useSupportTicketsList';

/**
 * What the AI triage bot handed to the support team.
 *
 * A queue rather than a mailbox: the server sorts it by status and then by how
 * long each one has been waiting, so the top of the Open tab is always the
 * thing to do next. Nothing here re-sorts it.
 */
export const SupportTickets = () => {
  const user = useAuthenticatedAuthUser();
  const filters = useSupportTicketFilters();
  const { open: openCount } = useSupportTicketOpenCount();

  /* The picker wants the whole location, the URL only carries what it takes to
     restore one - so the name travels beside the id. Never null: see
     ALL_LOCATIONS. */
  const [location, setLocation] = useState<SimpleLocationEntity>(
    filters.locationId
      ? ({
          id: filters.locationId,
          name: filters.locationName ?? '',
        } as SimpleLocationEntity)
      : ALL_LOCATIONS
  );

  const list = useSupportTicketsList({
    tab: filters.tab,
    type: filters.type,
    locationId: filters.locationId,
    viewerId: user.id,
  });

  const [sentinelRef, sentinelInView] = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (sentinelInView && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage();
    }
  }, [sentinelInView, list]);

  const { ticket } = useSupportTicket(filters.ticketId);
  const { claim, claimingId } = useClaimTicket();

  return (
    <>
      <AdminHeader title="Support Tickets" />

      <TabsWrapper>
        {SUPPORT_TICKET_TABS.map((tab) => (
          <Tab
            key={tab.value}
            selected={filters.tab === tab.value}
            onClick={() => filters.setTab(tab.value)}
          >
            {/* Only the Open tab wears the number: it is the queue, and the
                same count the sidebar badge shows. */}
            {tab.value === 'open' ? `${tab.label} (${openCount})` : tab.label}
          </Tab>
        ))}
      </TabsWrapper>

      <SupportTicketFilters
        type={filters.type}
        onTypeChange={filters.setType}
        location={location}
        setLocation={setLocation}
        onLocationPicked={(picked) =>
          filters.setLocation(picked?.id ?? null, picked?.name)
        }
      />

      {list.isLoading && <AdminSpinner />}

      {list.tickets.map((row) => (
        <SupportTicketRow
          key={row.id}
          ticket={row}
          onOpen={filters.openTicket}
          onClaim={claim}
          claiming={claimingId === row.id}
        />
      ))}

      {!list.isLoading && list.tickets.length === 0 && (
        <NoDataComponent
          title="Nothing here"
          description="No tickets match these filters."
        />
      )}

      <ListSpinner
        loading={list.isFetchingNextPage}
        observerRef={sentinelRef}
      />

      <SupportTicketModal
        isOpen={filters.ticketId !== null}
        ticket={ticket}
        onClose={() => filters.openTicket(null)}
      />
    </>
  );
};
